import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';

import { PaletteIcon } from '@common_components/icons/palette.icon';
import { useTranslation } from 'react-i18next';

interface ColorPickerInputProps {
  className?: string;
  width?: string;
  labelPosition?: 'top' | 'left';
  disable?: boolean;
  label?: string;
  isRequired?: boolean;
  color: string;
  onChange: ( color: string ) => void;
}

export const ColorPickerInput: React.FC<ColorPickerInputProps> =
( {
  color,
  onChange,
  label,
  isRequired,
  className,
  disable,
  labelPosition,
  width = 'w-full',
} ) => {
  const { t } = useTranslation();

  const popover = useRef<HTMLDivElement>( null );
  const container = useRef<HTMLDivElement>( null );
  const [ isOpen, toggle ] = useState( false );
  const [ isValid, setIsValid ] = useState<boolean>( false );

  const close = useCallback( () => {
    toggle( false );
  }, [] );

  const handleDocumentClick = useCallback( ( event: MouseEvent | TouchEvent ) => {
    const target = event.target as Node | null;
    if ( target && popover.current && !popover.current.contains( target ) && container.current && !container.current.contains( target ) ) {
      close();
    }
  }, [ close ] );

  useEffect( () => {
    document.addEventListener( 'mousedown', handleDocumentClick, true );
    document.addEventListener( 'touchstart', handleDocumentClick, true );

    return () => {
      document.removeEventListener( 'mousedown', handleDocumentClick, true );
      document.removeEventListener( 'touchstart', handleDocumentClick, true );
    };
  }, [ handleDocumentClick ] );

  const handleBlur = useCallback( ( colorChange: string ) => {
    setIsValid( true );
    if ( colorChange !== color ) {
      onChange( colorChange );
    }
  }, [ color, onChange, setIsValid ] );

  return (
    <div ref={container} className={`w-full min-w-36 rounded-md ${className} ${labelPosition === 'left' ? 'flex' : ''}`}>
      {/* Label */}
      {label && (
        <label className={`text-gray-600 w-3/12 mr-4 ${labelPosition === 'left' ? 'flex items-center' : ''}`}>
          {label}
        </label>
      )}
      {/* Input */}
      <div
        className={`relative flex gap-2 items-center
        ${disable && 'bg-gray-100 text-gray-500 cursor-not-allowed'} ${width}`}
        onClick={() => {
          if ( disable ) {
            return;
          }
          toggle( true );
        }}
      >
        {color == '' && (
          <div
            className={`h-full w-full z-20 p-0.5 absolute pointer-events-none ${disable ? 'cursor-not-allowed' : 'cursor-text'}`}
          >
            <div className="h-full w-full flex items-center px-2 gap-2 rounded-md bg-white">
              <PaletteIcon size={5} />
              <p>{t( 'common:pick_a_color' )}</p>
            </div>
          </div>
        )}
        <div className="absolute left-0 gap-3 p-3 flex items-center h-full">
          <div
            className={`swatch h-full rounded-md ${disable ? 'cursor-not-allowed' : 'cursor-pointer'} aspect-square`}
            style={{ backgroundColor: color }}
          />
          <p className="text-gray-base-dark pointer-events-none">#</p>
        </div>
        <HexColorInput
          color={color}
          onChange={handleBlur}
          disabled={disable}
          className={`py-2 px-2 pl-14 focus:border-black focus:ring-1 focus:ring-black rounded-md border outline-none ${
            !disable && 'hover:border-black'
          } ${
            disable &&
            'bg-gray-base-veryLight border-gray-base-veryLight text-gray-500 cursor-not-allowed'
          } ${width}`}
        />
        {/* Required Dot */}
        <div
          className={`w-4 h-4 rounded-full absolute -top-1 -right-2 trans-fast ${
            isRequired && !isValid
              ? 'bg-red-400 border-white border-2'
              : 'bg-transparent border-none'
          }`}
        />
        {/* Modal Color Select */}
        {isOpen && (
          <div
            className="absolute left-0 top-full mt-2 overflow-hidden border rounded-xl shadow-md z-50 bg-white"
            ref={popover}
          >
            <HexColorPicker color={color} onChange={handleBlur} />
          </div>
        )}

      </div>
    </div>
  );
};
